import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Wrench,
  Plus,
  Loader2,
  Filter,
  Building2,
  Euro,
  ImagePlus,
  Trash2,
  Pencil,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { canAccessManutenzioni, canManageManutenzioni } from "../utils/manutenzioniAccess";
import { Link } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIE = [
  { value: "idraulico", label: "Idraulico" },
  { value: "elettrico", label: "Elettrico" },
  { value: "edile", label: "Edile" },
  { value: "informatico", label: "Informatico" },
  { value: "impianti", label: "Impianti" },
  { value: "altro", label: "Altro" },
];
const CATEGORIE_LABEL = Object.fromEntries(CATEGORIE.map((c) => [c.value, c.label]));

const PRIORITA = [
  { value: "bassa", label: "Bassa" },
  { value: "normale", label: "Normale" },
  { value: "alta", label: "Alta" },
  { value: "urgente", label: "Urgente" },
];
const PRIORITA_BADGE = {
  bassa: "bg-slate-100 text-slate-700 border-slate-200",
  normale: "bg-sky-100 text-sky-800 border-sky-200",
  alta: "bg-amber-100 text-amber-800 border-amber-200",
  urgente: "bg-rose-100 text-rose-800 border-rose-200",
};

const STATI = [
  { value: "aperta", label: "Aperta" },
  { value: "in_corso", label: "In corso" },
  { value: "chiusa", label: "Chiusa" },
  { value: "annullata", label: "Annullata" },
];
const STATI_BADGE = {
  aperta: "bg-sky-100 text-sky-800 border-sky-200",
  in_corso: "bg-amber-100 text-amber-800 border-amber-200",
  chiusa: "bg-emerald-100 text-emerald-800 border-emerald-200",
  annullata: "bg-slate-100 text-slate-600 border-slate-200",
};

const emptyRichiesta = {
  titolo: "",
  descrizione: "",
  ubicazione: "",
  categoria: "altro",
  priorita: "normale",
  note: "",
};

const emptyFornitore = {
  nome: "",
  telefono: "",
  email: "",
  categorie: [],
  note: "",
  attivo: true,
};

function AuthImg({ url, token, alt, className }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!url || !token) return undefined;
    let objectUrl;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}${url}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok || cancelled) return;
        const blob = await res.blob();
        objectUrl = URL.createObjectURL(blob);
        if (!cancelled) setSrc(objectUrl);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url, token]);
  if (!src) return <div className={`bg-slate-100 ${className}`} />;
  return <img src={src} alt={alt} className={className} />;
}

export default function Manutenzioni() {
  const { user, token, activeStruttura } = useAuth();
  const canManage = canManageManutenzioni(user);
  // Modulo attivabile per struttura: se spento non si chiama l'API (risponderebbe 403).
  const moduleEnabled = canAccessManutenzioni(user);

  const [tab, setTab] = useState("richieste");
  const [loading, setLoading] = useState(true);
  const [richieste, setRichieste] = useState([]);
  const [fornitori, setFornitori] = useState([]);
  const [riepilogo, setRiepilogo] = useState(null);

  const [filtroStato, setFiltroStato] = useState("tutti");
  const [filtroPriorita, setFiltroPriorita] = useState("tutti");
  const [filtroCategoria, setFiltroCategoria] = useState("tutti");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyRichiesta);
  const [manageFields, setManageFields] = useState({
    stato: "aperta",
    fornitore_id: "",
    costo: "",
    data_esecuzione: "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [detailAllegati, setDetailAllegati] = useState([]);

  const [fornDialogOpen, setFornDialogOpen] = useState(false);
  const [fornEditId, setFornEditId] = useState(null);
  const [fornForm, setFornForm] = useState(emptyFornitore);

  const now = useMemo(() => new Date(), []);
  const [speseAnno, setSpeseAnno] = useState(now.getFullYear());
  const [speseMese, setSpeseMese] = useState(now.getMonth() + 1);

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token]
  );

  const loadRichieste = useCallback(async () => {
    if (!token) return;
    const params = {};
    if (filtroStato !== "tutti") params.stato = filtroStato;
    if (filtroPriorita !== "tutti") params.priorita = filtroPriorita;
    if (filtroCategoria !== "tutti") params.categoria = filtroCategoria;
    const { data } = await axios.get(`${API_URL}/api/manutenzioni/richieste`, {
      headers,
      params,
    });
    setRichieste(data.data || []);
  }, [token, headers, filtroStato, filtroPriorita, filtroCategoria]);

  const loadFornitori = useCallback(async () => {
    if (!token || !canManage) return;
    const { data } = await axios.get(`${API_URL}/api/manutenzioni/fornitori`, { headers });
    setFornitori(data.data || []);
  }, [token, headers, canManage]);

  const loadRiepilogo = useCallback(async () => {
    if (!token || !canManage) return;
    const { data } = await axios.get(`${API_URL}/api/manutenzioni/spese/riepilogo`, {
      headers,
      params: { anno: speseAnno, mese: speseMese },
    });
    setRiepilogo(data);
  }, [token, headers, canManage, speseAnno, speseMese]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      await loadRichieste();
      if (canManage) {
        await Promise.all([loadFornitori(), loadRiepilogo()]);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore caricamento manutenzioni");
    } finally {
      setLoading(false);
    }
  }, [loadRichieste, loadFornitori, loadRiepilogo, canManage]);

  useEffect(() => {
    if (!moduleEnabled) {
      setLoading(false);
      return;
    }
    reload();
  }, [reload, activeStruttura?.id, moduleEnabled]);

  const openCreate = () => {
    setEditId(null);
    setForm(emptyRichiesta);
    setManageFields({ stato: "aperta", fornitore_id: "", costo: "", data_esecuzione: "" });
    setDetailAllegati([]);
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditId(item.id);
    setForm({
      titolo: item.titolo || "",
      descrizione: item.descrizione || "",
      ubicazione: item.ubicazione || "",
      categoria: item.categoria || "altro",
      priorita: item.priorita || "normale",
      note: item.note || "",
    });
    setManageFields({
      stato: item.stato || "aperta",
      fornitore_id: item.fornitore_id || "",
      costo: item.costo != null ? String(item.costo) : "",
      data_esecuzione: item.data_esecuzione || "",
    });
    setDetailAllegati(item.allegati || []);
    setDialogOpen(true);
  };

  const saveRichiesta = async () => {
    if (!form.titolo.trim()) {
      toast.error("Inserisci un titolo");
      return;
    }
    setSaving(true);
    try {
      if (editId) {
        const payload = { ...form };
        if (canManage) {
          payload.stato = manageFields.stato;
          payload.fornitore_id = manageFields.fornitore_id || null;
          payload.costo =
            manageFields.costo === "" || manageFields.costo == null
              ? null
              : Number(manageFields.costo);
          payload.data_esecuzione = manageFields.data_esecuzione || null;
        }
        const { data } = await axios.patch(
          `${API_URL}/api/manutenzioni/richieste/${editId}`,
          payload,
          { headers }
        );
        setDetailAllegati(data.data?.allegati || detailAllegati);
        toast.success("Richiesta aggiornata");
        await loadRichieste();
        if (canManage) await loadRiepilogo();
        setDialogOpen(false);
      } else {
        const { data } = await axios.post(`${API_URL}/api/manutenzioni/richieste`, form, {
          headers,
        });
        setEditId(data.data?.id || null);
        setDetailAllegati(data.data?.allegati || []);
        toast.success("Richiesta creata: puoi allegare foto");
        await loadRichieste();
        if (canManage) await loadRiepilogo();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const uploadFoto = async (file) => {
    if (!editId || !file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(
        `${API_URL}/api/manutenzioni/richieste/${editId}/allegati`,
        fd,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDetailAllegati((prev) => [...prev, data.data]);
      await loadRichieste();
      toast.success("Foto caricata");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore upload foto");
    } finally {
      setUploading(false);
    }
  };

  const deleteAllegato = async (allegatoId) => {
    if (!editId || !canManage) return;
    try {
      await axios.delete(
        `${API_URL}/api/manutenzioni/richieste/${editId}/allegati/${allegatoId}`,
        { headers }
      );
      setDetailAllegati((prev) => prev.filter((a) => a.id !== allegatoId));
      await loadRichieste();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore eliminazione");
    }
  };

  const openFornCreate = () => {
    setFornEditId(null);
    setFornForm(emptyFornitore);
    setFornDialogOpen(true);
  };

  const openFornEdit = (f) => {
    setFornEditId(f.id);
    setFornForm({
      nome: f.nome || "",
      telefono: f.telefono || "",
      email: f.email || "",
      categorie: f.categorie || [],
      note: f.note || "",
      attivo: f.attivo !== false,
    });
    setFornDialogOpen(true);
  };

  const saveFornitore = async () => {
    if (!fornForm.nome.trim()) {
      toast.error("Nome fornitore obbligatorio");
      return;
    }
    setSaving(true);
    try {
      if (fornEditId) {
        await axios.patch(
          `${API_URL}/api/manutenzioni/fornitori/${fornEditId}`,
          fornForm,
          { headers }
        );
        toast.success("Fornitore aggiornato");
      } else {
        await axios.post(`${API_URL}/api/manutenzioni/fornitori`, fornForm, { headers });
        toast.success("Fornitore creato");
      }
      setFornDialogOpen(false);
      await loadFornitori();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio fornitore");
    } finally {
      setSaving(false);
    }
  };

  const deleteFornitore = async (id) => {
    if (!window.confirm("Eliminare questo fornitore?")) return;
    try {
      await axios.delete(`${API_URL}/api/manutenzioni/fornitori/${id}`, { headers });
      toast.success("Fornitore eliminato");
      await loadFornitori();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore eliminazione");
    }
  };

  if (!moduleEnabled) {
    return (
      <div className="p-4 md:p-6 lg:p-8 max-w-screen-2xl mx-auto w-full space-y-4">
        <PageHeader
          title="Manutenzioni"
          subtitle="Richieste di intervento, fornitori e spese di manutenzione"
          icon={Wrench}
          iconContainerClassName="bg-amber-100"
          iconClassName="text-amber-700"
        />
        <Card className="card-base" data-testid="manutenzioni-non-attivo">
          <CardHeader>
            <CardTitle>Modulo non attivo</CardTitle>
            <CardDescription>
              Il modulo Manutenzioni non è attivo per questa struttura.
              {canManage
                ? " Puoi attivarlo da Impostazioni."
                : " Chiedi a un responsabile o all'amministrativo di attivarlo."}
            </CardDescription>
          </CardHeader>
          {canManage ? (
            <CardContent>
              <Button asChild variant="outline" className="min-h-[44px]">
                <Link to="/impostazioni">Vai a Impostazioni</Link>
              </Button>
            </CardContent>
          ) : null}
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-screen-2xl mx-auto w-full space-y-4">
      <PageHeader
        title="Manutenzioni"
        subtitle="Richieste di intervento, fornitori e spese di manutenzione"
        icon={Wrench}
        iconContainerClassName="bg-amber-100"
        iconClassName="text-amber-700"
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="richieste">Richieste</TabsTrigger>
          {canManage ? <TabsTrigger value="fornitori">Fornitori</TabsTrigger> : null}
          {canManage ? <TabsTrigger value="spese">Spese</TabsTrigger> : null}
        </TabsList>

        <TabsContent value="richieste" className="space-y-4 mt-4">
          <Card className="card-base">
            <CardContent className="p-4 flex flex-col md:flex-row gap-3 md:items-end">
              <div className="flex items-center gap-2 text-slate-500 text-sm shrink-0">
                <Filter className="w-4 h-4" /> Filtri
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 flex-1">
                <div>
                  <Label className="text-xs">Stato</Label>
                  <Select value={filtroStato} onValueChange={setFiltroStato}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tutti">Tutti</SelectItem>
                      {STATI.map((s) => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Priorità</Label>
                  <Select value={filtroPriorita} onValueChange={setFiltroPriorita}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tutti">Tutte</SelectItem>
                      {PRIORITA.map((s) => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Categoria</Label>
                  <Select value={filtroCategoria} onValueChange={setFiltroCategoria}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tutti">Tutte</SelectItem>
                      {CATEGORIE.map((s) => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button onClick={openCreate} className="bg-amber-600 hover:bg-amber-700">
                <Plus className="w-4 h-4 mr-1" /> Nuova richiesta
              </Button>
            </CardContent>
          </Card>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
            </div>
          ) : richieste.length === 0 ? (
            <Card className="card-base">
              <CardContent className="p-8 text-center text-slate-500">
                Nessuna richiesta di manutenzione. Creane una per iniziare.
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {richieste.map((r) => (
                <Card
                  key={r.id}
                  className="card-base cursor-pointer hover:border-amber-200 transition-colors"
                  onClick={() => openEdit(r)}
                >
                  <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <p className="font-semibold text-slate-900 truncate">{r.titolo}</p>
                        <Badge className={STATI_BADGE[r.stato] || STATI_BADGE.aperta}>
                          {STATI.find((s) => s.value === r.stato)?.label || r.stato}
                        </Badge>
                        <Badge className={PRIORITA_BADGE[r.priorita] || PRIORITA_BADGE.normale}>
                          {PRIORITA.find((s) => s.value === r.priorita)?.label || r.priorita}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-500 truncate">
                        {CATEGORIE_LABEL[r.categoria] || r.categoria}
                        {r.ubicazione ? ` · ${r.ubicazione}` : ""}
                        {r.creato_da_nome ? ` · ${r.creato_da_nome}` : ""}
                      </p>
                    </div>
                    <div className="text-right text-sm text-slate-500 shrink-0">
                      {canManage && r.costo != null ? (
                        <p className="font-medium text-slate-800">€ {Number(r.costo).toFixed(2)}</p>
                      ) : null}
                      <p>{r.data_richiesta || "—"}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {canManage ? (
          <TabsContent value="fornitori" className="space-y-4 mt-4">
            <div className="flex justify-end">
              <Button onClick={openFornCreate} className="bg-amber-600 hover:bg-amber-700">
                <Plus className="w-4 h-4 mr-1" /> Nuovo fornitore
              </Button>
            </div>
            {fornitori.length === 0 ? (
              <Card className="card-base">
                <CardContent className="p-8 text-center text-slate-500">
                  Nessun fornitore in anagrafica.
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {fornitori.map((f) => (
                  <Card key={f.id} className="card-base">
                    <CardContent className="p-4 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-semibold text-slate-900 flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-slate-400" />
                          {f.nome}
                          {!f.attivo ? (
                            <Badge className="bg-slate-100 text-slate-600">Disattivo</Badge>
                          ) : null}
                        </p>
                        <p className="text-sm text-slate-500">
                          {[f.telefono, f.email].filter(Boolean).join(" · ") || "—"}
                          {(f.categorie || []).length
                            ? ` · ${(f.categorie || []).map((c) => CATEGORIE_LABEL[c] || c).join(", ")}`
                            : ""}
                        </p>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <Button variant="ghost" size="icon" onClick={() => openFornEdit(f)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => deleteFornitore(f.id)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        ) : null}

        {canManage ? (
          <TabsContent value="spese" className="space-y-4 mt-4">
            <Card className="card-base">
              <CardHeader className="p-4 pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Euro className="w-4 h-4 text-emerald-600" /> Riepilogo spese
                </CardTitle>
                <CardDescription>Totale interventi con costo nel mese selezionato</CardDescription>
              </CardHeader>
              <CardContent className="p-4 pt-0 space-y-4">
                <div className="flex flex-wrap gap-3 items-end">
                  <div>
                    <Label className="text-xs">Anno</Label>
                    <Input
                      type="number"
                      value={speseAnno}
                      onChange={(e) => setSpeseAnno(Number(e.target.value) || now.getFullYear())}
                      className="w-28"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Mese</Label>
                    <Select
                      value={String(speseMese)}
                      onValueChange={(v) => setSpeseMese(Number(v))}
                    >
                      <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                          <SelectItem key={m} value={String(m)}>
                            {new Date(2000, m - 1, 1).toLocaleString("it-IT", { month: "long" })}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button variant="outline" onClick={loadRiepilogo}>Aggiorna</Button>
                </div>
                <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-4">
                  <p className="text-sm text-emerald-700">Totale mese</p>
                  <p className="text-2xl font-semibold text-emerald-900">
                    € {(riepilogo?.totale ?? 0).toFixed(2)}
                  </p>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-slate-700 mb-2">Per categoria</p>
                    {(riepilogo?.per_categoria || []).length === 0 ? (
                      <p className="text-sm text-slate-400">Nessun dato</p>
                    ) : (
                      <ul className="space-y-1 text-sm">
                        {riepilogo.per_categoria.map((row) => (
                          <li key={row.categoria} className="flex justify-between">
                            <span>{CATEGORIE_LABEL[row.categoria] || row.categoria}</span>
                            <span className="font-medium">€ {row.totale.toFixed(2)}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-700 mb-2">Per fornitore</p>
                    {(riepilogo?.per_fornitore || []).length === 0 ? (
                      <p className="text-sm text-slate-400">Nessun dato</p>
                    ) : (
                      <ul className="space-y-1 text-sm">
                        {riepilogo.per_fornitore.map((row) => (
                          <li key={row.fornitore} className="flex justify-between gap-2">
                            <span className="truncate">{row.fornitore}</span>
                            <span className="font-medium shrink-0">€ {row.totale.toFixed(2)}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        ) : null}
      </Tabs>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editId ? "Dettaglio richiesta" : "Nuova richiesta"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div>
              <Label>Titolo *</Label>
              <Input
                value={form.titolo}
                onChange={(e) => setForm((p) => ({ ...p, titolo: e.target.value }))}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Descrizione</Label>
              <Textarea
                value={form.descrizione}
                onChange={(e) => setForm((p) => ({ ...p, descrizione: e.target.value }))}
                className="mt-1"
                rows={3}
              />
            </div>
            <div>
              <Label>Ubicazione</Label>
              <Input
                value={form.ubicazione}
                onChange={(e) => setForm((p) => ({ ...p, ubicazione: e.target.value }))}
                placeholder="Camera, zona, impianto…"
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Categoria</Label>
                <Select
                  value={form.categoria}
                  onValueChange={(v) => setForm((p) => ({ ...p, categoria: v }))}
                >
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIE.map((c) => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priorità</Label>
                <Select
                  value={form.priorita}
                  onValueChange={(v) => setForm((p) => ({ ...p, priorita: v }))}
                >
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PRIORITA.map((c) => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Note</Label>
              <Textarea
                value={form.note}
                onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))}
                className="mt-1"
                rows={2}
              />
            </div>

            {canManage && editId ? (
              <div className="rounded-lg border border-slate-200 p-3 space-y-3 bg-slate-50/50">
                <p className="text-sm font-medium text-slate-800">Gestione</p>
                <div>
                  <Label>Stato</Label>
                  <Select
                    value={manageFields.stato}
                    onValueChange={(v) => setManageFields((p) => ({ ...p, stato: v }))}
                  >
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATI.map((s) => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Fornitore</Label>
                  <Select
                    value={manageFields.fornitore_id || "none"}
                    onValueChange={(v) =>
                      setManageFields((p) => ({ ...p, fornitore_id: v === "none" ? "" : v }))
                    }
                  >
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nessuno</SelectItem>
                      {fornitori.filter((f) => f.attivo).map((f) => (
                        <SelectItem key={f.id} value={f.id}>{f.nome}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Costo (€)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={manageFields.costo}
                      onChange={(e) => setManageFields((p) => ({ ...p, costo: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Data esecuzione</Label>
                    <Input
                      type="date"
                      value={manageFields.data_esecuzione || ""}
                      onChange={(e) =>
                        setManageFields((p) => ({ ...p, data_esecuzione: e.target.value }))
                      }
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
            ) : null}

            {editId ? (
              <div className="space-y-2">
                <Label>Foto</Label>
                <div className="flex flex-wrap gap-2">
                  {detailAllegati.map((a) => (
                    <div key={a.id} className="relative w-20 h-20 rounded-lg overflow-hidden border">
                      <AuthImg
                        url={a.preview_url}
                        token={token}
                        alt={a.file_name}
                        className="w-full h-full object-cover"
                      />
                      {canManage ? (
                        <button
                          type="button"
                          className="absolute top-0.5 right-0.5 bg-white/90 rounded p-0.5"
                          onClick={() => deleteAllegato(a.id)}
                        >
                          <Trash2 className="w-3 h-3 text-red-500" />
                        </button>
                      ) : null}
                    </div>
                  ))}
                  <label className="w-20 h-20 rounded-lg border border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 cursor-pointer hover:bg-slate-50">
                    {uploading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <ImagePlus className="w-5 h-5" />
                        <span className="text-[10px] mt-0.5">Aggiungi</span>
                      </>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={uploading}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) uploadFoto(f);
                        e.target.value = "";
                      }}
                    />
                  </label>
                </div>
              </div>
            ) : (
              <p className="text-xs text-slate-500">
                Dopo la creazione potrai allegare foto alla richiesta.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Chiudi</Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700"
              onClick={saveRichiesta}
              disabled={saving}
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              {editId ? "Salva" : "Crea"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={fornDialogOpen} onOpenChange={setFornDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{fornEditId ? "Modifica fornitore" : "Nuovo fornitore"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div>
              <Label>Nome *</Label>
              <Input
                value={fornForm.nome}
                onChange={(e) => setFornForm((p) => ({ ...p, nome: e.target.value }))}
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Telefono</Label>
                <Input
                  value={fornForm.telefono}
                  onChange={(e) => setFornForm((p) => ({ ...p, telefono: e.target.value }))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  value={fornForm.email}
                  onChange={(e) => setFornForm((p) => ({ ...p, email: e.target.value }))}
                  className="mt-1"
                />
              </div>
            </div>
            <div>
              <Label>Note</Label>
              <Textarea
                value={fornForm.note}
                onChange={(e) => setFornForm((p) => ({ ...p, note: e.target.value }))}
                className="mt-1"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFornDialogOpen(false)}>Annulla</Button>
            <Button className="bg-amber-600 hover:bg-amber-700" onClick={saveFornitore} disabled={saving}>
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
